/**
 * HubSpot OAuth Callback Page
 * Handles the OAuth redirect from HubSpot
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import api, { setToken } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import vizzexLogo from '../../assets/VIZZEX.250.png';

type CallbackStatus = 'loading' | 'success' | 'error';

interface CallbackState {
  status: CallbackStatus;
  message: string;
  isNewUser?: boolean;
}

export default function OAuthCallbackPage() {
  const [state, setState] = useState<CallbackState>({
    status: 'loading',
    message: 'Connecting to HubSpot...'
  });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Check for OAuth errors
      if (error) {
        setState({
          status: 'error',
          message: errorDescription || `OAuth error: ${error}`
        });
        return;
      }

      // Check for authorization code
      if (!code) {
        setState({
          status: 'error',
          message: 'No authorization code received from HubSpot'
        });
        return;
      }

      try {
        setState({ status: 'loading', message: 'Authenticating with HubSpot...' });

        // Exchange code for tokens via our backend
        const response = await api.get('/api/auth/oauth/callback/hubspot', {
          params: { code }
        });

        if (response.data.success && response.data.token) {
          // Store the token
          setToken(response.data.token);

          setState({
            status: 'success',
            message: response.data.isNewUser 
              ? 'Account created successfully!' 
              : 'Connected to HubSpot!',
            isNewUser: response.data.isNewUser
          });

          // Refresh user data
          await refreshUser();

          // Redirect to dashboard after a short delay
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 1500);
        } else {
          throw new Error(response.data.error || 'Authentication failed');
        }
      } catch (err) {
        console.error('OAuth callback error:', err);
        const error = err as { response?: { data?: { error?: string } }; message?: string };
        setState({
          status: 'error',
          message: error.response?.data?.error || error.message || 'Failed to connect to HubSpot'
        });
      }
    };

    handleCallback();
  }, [searchParams, navigate, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md animate-slide-up">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src={vizzexLogo} 
            alt="VizzEx Pro" 
            className="w-[250px] mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold gradient-text">VizzEx Pro</h1>
        </div>

        {/* Status Card */}
        <div className="glass rounded-2xl p-8 text-center">
          {state.status === 'loading' && (
            <>
              <div className="w-16 h-16 rounded-full bg-[var(--color-primary-500)]/20 flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-8 h-8 text-[var(--color-primary-400)] animate-spin" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Connecting...</h2>
              <p className="text-[var(--text-secondary)]">{state.message}</p>
            </>
          )}

          {state.status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-full bg-[var(--color-success)]/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-[var(--color-success)]" />
              </div>
              <h2 className="text-xl font-semibold mb-2">{state.message}</h2>
              <p className="text-[var(--text-secondary)]">
                {state.isNewUser 
                  ? 'Your VizzEx Pro account has been created and linked to HubSpot.'
                  : 'Your HubSpot portal has been connected.'
                }
              </p>
              <p className="text-sm text-white/70 mt-4">
                Redirecting to dashboard...
              </p>
            </>
          )}

          {state.status === 'error' && (
            <>
              <div className="w-16 h-16 rounded-full bg-[var(--color-error)]/20 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-[var(--color-error)]" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Connection Failed</h2>
              <p className="text-[var(--text-secondary)] mb-6">{state.message}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => navigate('/login')}
                  className="px-6 py-3 bg-[var(--bg-tertiary)] text-white font-medium rounded-lg hover:bg-[var(--bg-tertiary)]/80 transition-colors"
                >
                  Back to Login
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-3 bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-600)] text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
                >
                  Try Again
                </button>
              </div>
            </>
          )}
        </div>

        {/* Help text */}
        {state.status === 'error' && (
          <div className="mt-6 p-4 bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/30 rounded-lg">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-[var(--color-warning)] flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-[var(--color-warning)]">Need help?</p>
                <p className="text-[var(--text-secondary)] mt-1">
                  Make sure you have authorized VizzEx Pro to access your HubSpot portal. 
                  If the problem persists, please contact support.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


